import React, { useEffect, useState } from 'react';
import { FiCheck, FiCornerUpLeft, FiEdit2, FiEyeOff, FiSend, FiX } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import styles from './StudentAskBar.module.css';

function useDisplayName() {
  try {
    const { user } = useAuth(); // eslint-disable-line react-hooks/rules-of-hooks
    const first = user?.firstName || user?.first_name || '';
    const last = user?.lastName || user?.last_name || '';
    return [first, last ? `${last[0]}.` : ''].filter(Boolean).join(' ') || 'My name';
  } catch (e) {
    return 'My name';
  }
}

// Chat-style ask bar. The eye button inside the field toggles Post Anonymously
// (on by default); the round button sends.
export default function StudentAskBar({ posting, onAsk, editing, onSaveEdit, onCancelEdit, replyingTo, onSendReply, onCancelReply }) {
  const [draft, setDraft] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const name = useDisplayName();

  // Editing your own question happens in this same box.
  useEffect(() => {
    setDraft(editing ? editing.body : '');
  }, [editing]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    if (editing) {
      await onSaveEdit(draft);
      return;
    }
    if (replyingTo) {
      onSendReply(draft, anonymous);
      setDraft('');
      return;
    }
    onAsk(draft, anonymous);
    setDraft('');
  };

  return (
    <div className={styles.wrap}>
      {editing && (
        <div className={styles.editBanner}>
          <FiEdit2 size={14} />
          <span className={styles.editText}><b>Editing your message</b>{editing.body}</span>
          <button type="button" className={styles.cancelEdit} onClick={onCancelEdit} aria-label="Cancel edit"><FiX size={16} /></button>
        </div>
      )}
      {replyingTo && (
        <div className={styles.editBanner}>
          <FiCornerUpLeft size={14} />
          <span className={styles.editText}><b>Replying to {replyingTo.authorName}</b>{replyingTo.body}</span>
          <button type="button" className={styles.cancelEdit} onClick={onCancelReply} aria-label="Cancel reply"><FiX size={16} /></button>
        </div>
      )}
      {!editing && (
        <div className={styles.sendingAs} role="group" aria-label="Sending as">
          <span className={styles.sendingLabel}>Sending as</span>
          <button type="button" className={`${styles.seg} ${!anonymous ? styles.segOn : ''}`} aria-pressed={!anonymous} onClick={() => setAnonymous(false)}>{name}</button>
          <button type="button" className={`${styles.seg} ${anonymous ? styles.segOn : ''}`} aria-pressed={anonymous} onClick={() => setAnonymous(true)}>Anonymous</button>
        </div>
      )}
      <form className={styles.bar} onSubmit={submit}>
        <div className={styles.field}>
          {!editing && (
            <button
              type="button"
              className={`${styles.anonBtn} ${anonymous ? styles.anonBtnOn : ''}`}
              aria-pressed={anonymous}
              aria-label="Post Anonymously"
              title="Post Anonymously"
              onClick={() => setAnonymous((v) => !v)}
            >
              <FiEyeOff size={16} />
            </button>
          )}
          <input
            type="text"
            className={styles.input}
            placeholder={replyingTo ? (anonymous ? 'Reply anonymously…' : 'Write a reply…') : (anonymous ? 'Ask anonymously…' : 'Ask a question…')}
            aria-label={editing ? 'Edit your message' : (replyingTo ? 'Write a reply' : 'Ask a question')}
            dir="auto"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            autoFocus={Boolean(editing || replyingTo)}
          />
        </div>
        <button type="submit" className={styles.send} aria-label={editing ? 'Save' : (replyingTo ? 'Reply' : 'Ask')} disabled={posting || !draft.trim()}>
          {editing ? <FiCheck size={18} /> : <FiSend size={18} />}
        </button>
      </form>
    </div>
  );
}
